/**
 * Deal Stage Constants
 *
 * Centralized definitions for deal stages used across the application.
 * Keep in sync with Postgres `deal_stage` enum (see supabase/migrations/20260605000000_migrate_deal_stage_enum_v2.sql).
 *
 * This is the CANONICAL definition of the stage vocabulary. Hub re-exports it from
 * `@/lib/constants/deal-stages` and `features/deals/types/deal-core.ts`, so there is
 * exactly one list — a stage added to the Postgres enum is added here and everything
 * that reasons about stages (Hub, the scorers below, iOS, reporting) sees it at once.
 */

/**
 * Deal pipeline stages. Mirrors the Postgres `deal_stage` enum exactly.
 *
 * Hand-written rather than generated: it is the shape the scorers, the board, and
 * the native clients all agree on, and a generated type could not live in a package
 * with no Supabase dependency.
 */
export type DealStage =
  | "lead"
  | "qualified"
  | "discovery"
  | "proposal"
  | "negotiation"
  | "contract_signed"
  | "deal_won"
  | "hand_off"
  | "started"
  | "in_progress"
  | "completed"
  | "invoiced"
  | "paid"
  | "sm_lost_no_savings"
  | "sm_lost_savings_customer_rejected"
  | "wtc_lost_solution_rejected"
  | "wtc_lost_over_budget"
  | "wtc_lost_no_budget";

/**
 * Sales pipeline — pre–deal-won hunting stages, as a literal tuple.
 *
 * The tuple form exists so consumers can derive the NARROW union of just these
 * six (`SalesPipelineStage`) rather than all 18 stages — next-email-client's
 * inbox deal card keys a label record on exactly this set. One literal, both
 * shapes, so the two can never disagree.
 */
export const SALES_PIPELINE_STAGE_TUPLE = [
  "lead",
  "qualified",
  "discovery",
  "proposal",
  "negotiation",
  "contract_signed",
] as const satisfies readonly DealStage[];

/** Just the pre-win hunting stages — NOT the post-win delivery ladder. */
export type SalesPipelineStage = (typeof SALES_PIPELINE_STAGE_TUPLE)[number];

/** Sales pipeline — pre–deal-won hunting stages */
export const SALES_PIPELINE_STAGES: DealStage[] = [...SALES_PIPELINE_STAGE_TUPLE];

/** Post–deal-won delivery and billing stages */
export const DELIVERY_STAGES: DealStage[] = [
  "deal_won",
  "hand_off",
  "started",
  "in_progress",
  "completed",
  "invoiced",
  "paid",
];

/** Closed deal stages (won + delivery) — legacy alias for delivery-focused views */
export const CLOSED_DEAL_STAGES: DealStage[] = [...DELIVERY_STAGES];

/**
 * Deals actively being worked — the post-sale execution window:
 * contract signed → started → in progress. (NOT yet completed/invoiced/paid.)
 * Spans SALES_PIPELINE_STAGES (`contract_signed`) and DELIVERY_STAGES
 * (`started`, `in_progress`), so it needs its own consolidated set.
 */
export const IN_PROGRESS_DEAL_STAGES: DealStage[] = [
  "contract_signed",
  "started",
  "in_progress",
];

export function isInProgressDealStage(stage: DealStage): boolean {
  return IN_PROGRESS_DEAL_STAGES.includes(stage);
}

/** Active pipeline stages (sales only — not won, delivery, or lost) */
export const ACTIVE_STAGES: DealStage[] = [...SALES_PIPELINE_STAGES];

/** Terminal lost-reason stages */
export const LOST_STAGES: DealStage[] = [
  "sm_lost_no_savings",
  "sm_lost_savings_customer_rejected",
  "wtc_lost_solution_rejected",
  "wtc_lost_over_budget",
  "wtc_lost_no_budget",
];

/** @deprecated Use LOST_STAGES — kept for callers using TERMINAL_STAGES */
export const TERMINAL_STAGES: DealStage[] = [...LOST_STAGES];

/** Stage after contract is signed — handoff to operations */
export const DEAL_WON_STAGE: DealStage = "deal_won";

/** Default stage when marking a deal lost until a specific reason is chosen */
export const DEFAULT_LOST_STAGE: DealStage = "wtc_lost_solution_rejected";

/** All stages for filters and validation */
export const ALL_STAGES: DealStage[] = [
  ...SALES_PIPELINE_STAGES,
  ...DELIVERY_STAGES,
  ...LOST_STAGES,
];

/** Tuple for Zod `z.enum()` — must match Postgres `deal_stage` */
export const DEAL_STAGE_VALUES = [
  "lead",
  "qualified",
  "discovery",
  "proposal",
  "negotiation",
  "contract_signed",
  "deal_won",
  "hand_off",
  "started",
  "in_progress",
  "completed",
  "invoiced",
  "paid",
  "sm_lost_no_savings",
  "sm_lost_savings_customer_rejected",
  "wtc_lost_solution_rejected",
  "wtc_lost_over_budget",
  "wtc_lost_no_budget",
] as const satisfies readonly DealStage[];

/** Stages included on account timeline summaries (all except lost) */
export const ACCOUNT_TIMELINE_STAGES: DealStage[] = [
  ...SALES_PIPELINE_STAGES,
  ...DELIVERY_STAGES,
];

export function isSalesPipelineStage(stage: DealStage): boolean {
  return SALES_PIPELINE_STAGES.includes(stage);
}

export function isClosedDealStage(stage: DealStage): boolean {
  return CLOSED_DEAL_STAGES.includes(stage);
}

export function isActiveStage(stage: DealStage): boolean {
  return ACTIVE_STAGES.includes(stage);
}

export function isDeliveryStage(stage: DealStage): boolean {
  return DELIVERY_STAGES.includes(stage);
}

export function isLostStage(stage: DealStage): boolean {
  return LOST_STAGES.includes(stage);
}

/**
 * Stages representing a deal that has been WON — won + all post-win delivery /
 * billing stages (`deal_won`, `hand_off`, `started`, `in_progress`, `completed`,
 * `invoiced`, `paid`).
 *
 * CRITICAL: a deal almost never SITS at `deal_won` — the moment it's won it
 * progresses to delivery (`in_progress`) and ultimately `paid`. So "is this deal
 * won?" / win-rate / ACV / won-count MUST test this whole set. Checking
 * `stage === "deal_won"` undercounts to ~0 (a real production bug: every
 * `=== "deal_won"` win metric read zero because no deal was parked there).
 */
export const WON_DEAL_STAGES: DealStage[] = [...CLOSED_DEAL_STAGES];

/** True when a deal's stage means it has been won (won + delivery/billing). */
export function isWonDeal(stage: DealStage): boolean {
  return isClosedDealStage(stage);
}

export function isTerminalStage(stage: DealStage): boolean {
  return isLostStage(stage);
}

export function isWonStage(stage: DealStage): boolean {
  return stage === DEAL_WON_STAGE;
}
